import { Component, Input } from '@angular/core';
import { DropdownOption } from '../../../controls/standard/dropdown-control/dropdown-control.component';
import { BaseFieldComponent } from '../base-field/base-field.component';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import { LocationService } from '../../../../services/location.service';
import { Client, Location } from '@mira-avant/mtlibs';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-location-field',
	standalone: true,
	imports: [AppModule, ZorroModule, ThirdPartyModule, AngularModule],
	templateUrl: './location-field.component.html',
	styleUrl: './location-field.component.scss',
})
export class LocationFieldComponent extends BaseFieldComponent {
	@Input('client') public client: Client | null | undefined;
	public options: DropdownOption[] = [];
	private sub?: Subscription;
	public LOCATIONS_PAGE_SIZE: number = 2;
	public totalLocations: number = 0;
	private locations: Location[] = [];
	private currentPage: number = 1;
	public selectedLocationUuid: string | null = null;

	constructor(private locationService: LocationService) {
		super();
	}

	ngOnChanges(): void {
		if (this.client) {
			this.loadPageOfLocations(1);
			this.updateTotals();
		}
	}

	public async updateTotals(): Promise<void> {
		if (!this.client) {
			throw new Error('No client selected');
		}

		await this.locationService.getTotalLocations(this.client.uuid).then((totalLocations: number) => {
			this.totalLocations = totalLocations;
		});
	}

	public async loadPageOfLocations(pageNumber: number): Promise<void> {
		if (!this.client) {
			throw new Error('No client selected');
		}

		try {
			const locations = await this.locationService.getByClient$(
				this.client.uuid,
				pageNumber - 1,
				this.LOCATIONS_PAGE_SIZE
			);

			this.locations = locations;
			this.currentPage = pageNumber;
			await this.convertLocationsToOptions();
		} catch (error) {
			console.error('Error loading locations:', error);
		}
	}

	private async convertLocationsToOptions(): Promise<void> {
		const selectedLocationUuid: string | null = this.formGroup?.value.locationUuid;
		const selectedLocationIsInList = this.locations.some(loc => loc.uuid === selectedLocationUuid);

		if (selectedLocationUuid && !selectedLocationIsInList) {
			const selectedLocation = await this.locationService.get$(selectedLocationUuid);
			if (selectedLocation) {
				this.locations.unshift(selectedLocation);
			}
		}

		this.options = this.locations.map((location: Location) => {
			return { value: location.uuid, label: location.name || 'Unknown' };
		});
	}

	public async search(search: string): Promise<void> {
		if (!this.client) {
			throw new Error('No client selected');
		}

		if (!search) {
			this.loadPageOfLocations(1);
			await this.updateTotals();
			return;
		}

		await this.locationService
			.searchLocations(this.client.uuid, search)
			.then((locations: Location[]) => {
				this.locations = locations;
				this.currentPage = 1;
				this.totalLocations = locations.length;

				return this.convertLocationsToOptions();
			})
			.catch(error => {
				console.error('Error searching locations:', error);
			});
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
